/**
 * Main Application Component
 * Module 07 - Dashboard UI
 * Section 08 - State Management
 * 
 * Sets up React Query, routing, and global providers
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { UploadProvider } from './contexts/UploadContext';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import EmailVerificationGate from './components/Auth/EmailVerificationGate';
import Layout from './components/Layout/Layout';
import {
  Login,
  Register,
  ForgotPassword,
  ResetPassword,
  VerifyEmail,
  CheckYourEmail,
  Dashboard,
  Models,
  ModelViewer,
  Embeds,
  Settings,
} from './pages';

/**
 * Main App component with all providers and routing
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <UploadProvider>
          <BrowserRouter>
            <AuthProvider>
              <EmailVerificationGate />
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/check-your-email" element={<CheckYourEmail />} />

                {/* Protected routes */}
                <Route element={<ProtectedRoute />}>
                  <Route element={<Layout />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/models" element={<Models />} />
                    <Route path="/models/:id/view" element={<ModelViewer />} />
                    <Route path="/embeds" element={<Embeds />} />
                    <Route path="/settings" element={<Settings />} />

                    {/* Default redirect */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  </Route>
                </Route>

                {/* Catch all - redirect to login */}
                <Route path="*" element={<Navigate to="/login" replace />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </UploadProvider>
      </ToastProvider>

      {/* React Query Devtools - only in development */}
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} position="bottom" />
      )}
    </QueryClientProvider>
  );
}

export default App;
